import { useNavigate, useSearch } from '@tanstack/react-router';
import { Button } from '@sara/ui/components/button';
import { useDialog } from '@sara/ui/hooks/use-dialog';
import { Plus } from 'lucide-react';
import type { CandidateListItemDto } from '@sara/api-client';
import { useActiveOrganization } from '../../contexts/auth/hooks/use-active-organization';
import { CandidateListContainer } from '../../contexts/candidates/containers/CandidateListContainer';
import { CreateInterviewDialog } from '../../contexts/interviews/containers/CreateInterviewDialog';
import { candidatesSearchSchema, type CandidatesSearch } from '../../router/tree';
import { PageHeader } from '../../components/page-header';

export function CandidatesPage() {
  const { organization } = useActiveOrganization();
  const createDialog = useDialog(CreateInterviewDialog);
  const search = useSearch({ from: '/authenticated/orgs/$orgSlug/candidates' });
  const navigate = useNavigate();

  return (
    <div className="flex min-h-[640px] flex-1 flex-col gap-12">
      <PageHeader
        title="Candidates"
        description="Browse the candidates your team has interviewed and start a new interview when you're ready."
        action={
          organization && (
            <Button
              variant="tinted"
              shape="pill"
              size="sm"
              className="h-8 gap-1.5 px-3 text-xs [&_svg]:size-3.5 sm:h-9 sm:px-4 sm:text-sm sm:[&_svg]:size-4"
              onClick={() => createDialog.open({ orgId: organization.id })}
            >
              <Plus strokeWidth={1.6} />
              Invite candidate to interview
            </Button>
          )
        }
      />

      <section className="flex min-h-0 flex-1 flex-col">
        {organization && (
          <CandidateListContainer
            orgId={organization.id}
            search={search}
            onSearchChange={(update: Partial<CandidatesSearch>) =>
              void navigate({
                to: '/orgs/$orgSlug/candidates',
                params: { orgSlug: organization.slug },
                search: (prev) =>
                  candidatesSearchSchema.parse({ ...prev, ...update }) as CandidatesSearch,
              })
            }
            onRowClick={(candidate: CandidateListItemDto) =>
              void navigate({
                to: '/orgs/$orgSlug/candidates/$candidateId',
                params: { orgSlug: organization.slug, candidateId: candidate.id },
              })
            }
          />
        )}
      </section>
    </div>
  );
}
